// @ts-nocheck
import { openDB, DBSchema } from 'idb';
import { ProjectAsset } from '../types';
import { v4 as uuidv4 } from 'uuid';
import GCPService from './gcp';

interface AppDB extends DBSchema {
  assets: {
    key: string;
    value: ProjectAsset & { blob?: Blob; gcpPath?: string }; // Store blob directly or GCP path
    indexes: { 'by-type': string };
  };
  creator3dProjects: {
    key: string;
    value: {
      id: string;
      name: string;
      thumbnail?: string;
      objects: Record<string, unknown>[]; // SceneObject[]
      background: string;
      lighting: string;
      cameraPosition: [number, number, number];
      cameraTarget: [number, number, number];
      bgColor: string;
      bgConfig: Record<string, unknown>;
      createdAt: number;
      updatedAt: number;
      gcpPath?: string; // GCP path if saved to cloud
    };
  };
  stagerScenes: {
    key: string;
    value: {
      id: string;
      name?: string;
      thumbnail?: string;
      objects: Record<string, unknown>[];
      background: string;
      lighting: string;
      updatedAt: number;
      gcpPath?: string; // GCP path if saved to cloud
    };
  };
  projects: {
    key: string;
    value: {
      id: string;
      name: string;
      type: 'video-compilation';
      data: Record<string, unknown>;
      createdAt: number;
      updatedAt: number;
      gcpPath?: string;
    };
  };
}

const DB_NAME = 'ai-creative-studio-db';
const DB_VERSION = 5; // Incremented for creator3dProjects store

export const initDB = async () => {
  return openDB<AppDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const store = db.createObjectStore('assets', { keyPath: 'id' });
        store.createIndex('by-type', 'type');
      }
      if (oldVersion < 2) {
        db.createObjectStore('stagerScenes', { keyPath: 'id' });
      }
      if (oldVersion < 3) {
        db.createObjectStore('projects', { keyPath: 'id' });
      }
      if (oldVersion < 4) {
        // Add creator3dProjects store and GCP path support
        if (!db.objectStoreNames.contains('creator3dProjects')) {
          db.createObjectStore('creator3dProjects', { keyPath: 'id' });
        }
      }
    },
  });
};

export const saveScene = async (scene: { 
  id: string; 
  objects: Record<string, unknown>[]; 
  background: string; 
  lighting: string; 
  backgroundSpeed?: number;
  backgroundLightColor?: string;
  backgroundDarkColor?: string;
  vantaConfig?: Record<string, unknown>;
  codeContent?: string;
  gcpPath?: string;
}) => {
  const db = await initDB();
  
  // If authenticated, save to GCP
  if (GCPService.isAuthenticated()) {
    try {
      const response = await GCPService.saveScene(`scene_${scene.id}`, {
        objects: scene.objects,
        lighting: scene.lighting,
        background: scene.background,
        backgroundSpeed: scene.backgroundSpeed,
        backgroundLightColor: scene.backgroundLightColor,
        backgroundDarkColor: scene.backgroundDarkColor,
        vantaConfig: scene.vantaConfig,
        codeContent: scene.codeContent,
        camera: { position: [0, 0, 0], target: [0, 0, 0] }
      });
      scene.gcpPath = response.path;
    } catch (error) {
      console.error('Failed to save scene to GCP:', error);
    }
  }
  
  await db.put('stagerScenes', { ...scene, updatedAt: Date.now() });
};

export const getScene = async (id: string): Promise<{ 
    id: string; 
    objects: Record<string, unknown>[]; 
    background: string; 
    lighting: string; 
    backgroundSpeed?: number;
    backgroundLightColor?: string;
    backgroundDarkColor?: string;
    vantaConfig?: Record<string, unknown>;
    codeContent?: string;
    gcpPath?: string; 
} | undefined> => {
  const db = await initDB();
  return db.get('stagerScenes', id);
};

export const saveAsset = async (asset: Omit<ProjectAsset, 'id' | 'createdAt'> & { id?: string; gcpPath?: string }) => {
  const db = await initDB();
  let blob: Blob | undefined;
  let gcpPath: string | undefined;

  // If URL is a blob URL and authenticated, upload to GCP
  if (asset.url.startsWith('blob:') && GCPService.isAuthenticated()) {
    try {
      const response = await fetch(asset.url);
      const fileBlob = await response.blob();
      // Map all asset types to valid GCP types
      const gcpType: 'model' | 'video' | 'image' | 'project' = 
        asset.type === 'model' ? 'model' :
        asset.type === 'video' ? 'video' :
        asset.type === 'image' ? 'image' :
        'project';
      
      const gcpResponse = await GCPService.uploadBlob(
        fileBlob,
        asset.name,
        gcpType,
        fileBlob.type
      );
      gcpPath = gcpResponse.path;
      asset.url = gcpResponse.signedUrl; // Update URL to signed URL
    } catch (error) {
      console.error('Failed to upload asset to GCP:', error);
      // Fallback to storing blob locally
      try {
        const response = await fetch(asset.url);
        blob = await response.blob();
      } catch (e) {
        console.error("Failed to fetch blob for persistence:", e);
      }
    }
  } else if (asset.url.startsWith('blob:')) {
    // No GCP auth - store blob locally
    try {
      const response = await fetch(asset.url);
      blob = await response.blob();
    } catch (e) {
      console.error("Failed to fetch blob for persistence:", e);
    }
  }

  const newAsset: ProjectAsset & { blob?: Blob; gcpPath?: string } = {
    ...asset,
    id: asset.id || uuidv4(),
    createdAt: Date.now(),
    blob,
    gcpPath
  };
  
  await db.put('assets', newAsset);
  return newAsset;
};

export const checkAssetExists = async (name: string, type: ProjectAsset['type']) => {
    const db = await initDB();
    const assets = await db.getAllFromIndex('assets', 'by-type', type);
    return assets.find(a => a.name === name);
};

export const getAssets = async (type?: ProjectAsset['type']) => {
  const db = await initDB();
  let assets;
  if (type) {
    assets = await db.getAllFromIndex('assets', 'by-type', type);
  } else {
    assets = await db.getAll('assets');
  }

  // Re-create blob URLs for display, prefer GCP URLs
  return assets.map(asset => {
    if (asset.gcpPath) {
      // Asset is hosted on GCP - use signed URL
      return asset;
    } else if (asset.blob) {
      return { ...asset, url: URL.createObjectURL(asset.blob) };
    }
    return asset;
  });
};

export const deleteAsset = async (id: string) => {
  const db = await initDB();
  const asset = await db.get('assets', id);
  
  // Delete from GCP if path exists
  if (asset?.gcpPath && GCPService.isAuthenticated()) {
    try {
      await GCPService.deleteFile(asset.gcpPath);
    } catch (error) {
      console.error('Failed to delete asset from GCP:', error);
    }
  }
  
  await db.delete('assets', id);
};

export const saveProject = async (project: { 
  name: string; 
  type: 'video-compilation'; 
  data: Record<string, unknown>; 
  id?: string;
  gcpPath?: string;
}) => {
    const db = await initDB();
    
    // If authenticated, save to GCP
    if (GCPService.isAuthenticated()) {
      try {
        const response = await GCPService.uploadBlob(
          new Blob([JSON.stringify(project.data)], { type: 'application/json' }),
          `${project.name}.project.json`,
          'project',
          'application/json'
        );
        project.gcpPath = response.path;
      } catch (error) {
        console.error('Failed to save project to GCP:', error);
      }
    }
    
    const newProject = {
        ...project,
        id: project.id || uuidv4(),
        createdAt: Date.now(),
        updatedAt: Date.now()
    };
    await db.put('projects', newProject);
    return newProject;
};

export const getProjects = async () => {
    const db = await initDB();
    return db.getAll('projects');
};

/**
 * Save a complete Creator3D project (all objects, animations, materials, settings)
 */
export const saveCreator3DProject = async (project: {
  name: string;
  objects: Record<string, unknown>[];
  background: string;
  lighting: string;
  cameraPosition: [number, number, number];
  cameraTarget: [number, number, number];
  bgColor: string;
  bgConfig: Record<string, unknown>;
  id?: string;
  thumbnail?: string;
}) => {
  const db = await initDB();
  
  // If authenticated, save to GCP
  let gcpPath: string | undefined;
  if (GCPService.isAuthenticated()) {
    try {
      const response = await GCPService.uploadBlob(
        new Blob([JSON.stringify(project)], { type: 'application/json' }),
        `${project.name}_creator3d.json`,
        'project',
        'application/json'
      );
      gcpPath = response.path;
    } catch (error) {
      console.error('Failed to save Creator3D project to GCP:', error);
    }
  }
  
  const newProject = {
    ...project,
    id: project.id || uuidv4(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    gcpPath,
  };
  
  await db.put('creator3dProjects', newProject);
  return newProject;
};

/**
 * Load a Creator3D project by ID
 */
export const getCreator3DProject = async (id: string) => {
  const db = await initDB();
  return db.get('creator3dProjects', id);
};

/**
 * Get all Creator3D projects
 */
export const getCreator3DProjects = async () => {
  const db = await initDB();
  return db.getAll('creator3dProjects');
};

/**
 * Delete a Creator3D project
 */
export const deleteCreator3DProject = async (id: string) => {
  const db = await initDB();
  const project = await db.get('creator3dProjects', id);
  
  // Delete from GCP if path exists
  if (project?.gcpPath && GCPService.isAuthenticated()) {
    try {
      await GCPService.deleteFile(project.gcpPath);
    } catch (error) {
      console.error('Failed to delete Creator3D project from GCP:', error);
    }
  }
  
  await db.delete('creator3dProjects', id);
};
